// Step 18 of the affiliate/teacher-partner roadmap: referral-link click tracking.
//
// A teacher's referral link (buildReferralLink() in constants.js) is
// https://app.speafy.com/?ref=<teacherUid> — the code IS the teacher's Firebase uid, not an
// opaque token (see that function's own comment for why). This module is the first leg of the
// pipeline sketched there: link click -> cookie -> Paddle custom_data -> the subscription
// webhook -> profile.referredBy. It ONLY captures and stores the code client-side; nothing here
// writes profile.referredBy or talks to Firestore — that happens later (step 20), once a real
// payment webhook can confirm the relationship server-side.
//
// Why a cookie (not localStorage): the click-to-signup gap needs to self-expire on its own —
// with a cookie that's a Max-Age, with localStorage it would need a stored timestamp plus a
// manual staleness check at every read site. 30 days is a generous but finite attribution
// window; it is UNRELATED to the 12-month commission cap, which counts from the student's
// signup date, not from this click (see the commission ledger schema).
// Why not HttpOnly / server-set: nothing here is sensitive (buildReferralLink's own comment:
// "a uid is not a secret"), and this value must be readable by client JS regardless, since the
// checkout step (step 19) needs to read it back to build Paddle's custom_data in the browser.
//
// Compliance note (flagged, not resolved here): landing/privacy.html's Cookies & Local Storage
// section currently documents two categories only — essential/auth (no consent needed) and
// analytics (consent-gated via the cookie banner) — and says nothing about a referral cookie.
// This is written as a narrow, single-purpose, first-party, non-cross-site-tracking cookie,
// which is commonly treated as "necessary for the transaction the visitor is here for" (same
// bucket as a shopping-cart cookie) rather than "analytics" — but that classification has not
// had a legal review, same open item as the teacher-terms "Draft — pending review" banner.

export const REFERRAL_COOKIE_NAME = "sf_ref";
export const REFERRAL_COOKIE_MAX_AGE_DAYS = 30;

// Firebase Auth uids are, in practice, alphanumeric (no observed use of other characters), but
// this is not a documented hard contract — kept loose (length bound + a safe charset) rather
// than pattern-matched to today's exact uid shape, so a future uid format is never silently
// rejected. Real validation ("is this actually a teacher") is deferred to the webhook (step 20)
// server-side, where it's cheap and authoritative; a garbage ref value captured here is
// harmless — it simply resolves to nothing at attribution time.
const REFERRAL_CODE_RE = /^[A-Za-z0-9_-]{1,128}$/;

export function isPlausibleReferralCode(value) {
  return typeof value === "string" && REFERRAL_CODE_RE.test(value);
}

// Cookie helpers scoped to this module — the app has never set a first-party cookie before
// (the __session cookie used by the key-gated affiliate landing pages is a separate,
// HttpOnly, server-set cookie in a different Cloud Function context entirely, not this SPA).
function readCookie(name) {
  const match = String(document.cookie || "")
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

function writeCookie(name, value, maxAgeDays) {
  const maxAgeSeconds = Math.round(maxAgeDays * 86400);
  // Deliberately no `Secure` flag: the referral code already travels in plaintext on every
  // navigation anyway (it's the `?ref=` query string itself, same value, same exposure) — a
  // `Secure`-only cookie would add no real protection against that, while browsers (and jsdom,
  // matching real behaviour) silently refuse to SET a `Secure` cookie over plain http, which
  // would break referral capture in local dev (CRA's default `npm start` serves http) for zero
  // actual security gain. SameSite=Lax still applies (blocks the cookie being sent cross-site).
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
}

// Reads `?ref=` off the given location.search (defaults to the real one) and, if it looks like
// a plausible referral code AND no code is already stored, writes it to the cookie.
//
// First-touch wins: an existing cookie is never overwritten by a later `?ref=` visit, whether
// it names the same teacher or a different one. Deliberate choice for a small, by-hand-onboarded
// partner program — a low-effort game (bookmark someone else's own link, click it while they're
// mid-signup) should not be able to steal credit from whichever teacher actually referred them
// first. If last-touch is ever wanted instead, this is the one place to change it.
//
// Returns the code that ended up stored (freshly captured or pre-existing), or null.
export function captureReferralFromLocation(search) {
  const existing = readCookie(REFERRAL_COOKIE_NAME);
  if (existing) return existing;

  const params = new URLSearchParams(search ?? window.location.search);
  const candidate = params.get("ref");
  if (!isPlausibleReferralCode(candidate)) return null;

  writeCookie(REFERRAL_COOKIE_NAME, candidate, REFERRAL_COOKIE_MAX_AGE_DAYS);
  return candidate;
}

// Reads back the currently-stored referral code, if any. Used by the checkout step (step 19) to
// populate Paddle's custom_data — kept as a separate named export so that call site doesn't need
// to know this is backed by a cookie at all.
export function getReferralCode() {
  return readCookie(REFERRAL_COOKIE_NAME);
}

// Step 19 helper: the referral code to actually SEND with a checkout, given who's checking out.
// Same idea as getReferralCode(), except it also refuses to hand back a self-referral — a
// teacher who visits (or is sent) their own link and then subscribes must never end up crediting
// themselves. This is the point in the pipeline where that check first becomes possible: step 18
// captures the cookie before anyone is necessarily signed in, so it can't compare against a uid
// it doesn't have yet. Checkout time always has one.
export function getReferralCodeForCheckout(currentUid) {
  const code = getReferralCode();
  if (!code) return null;
  if (currentUid && code === currentUid) return null;
  return code;
}
